import { useForm, Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { useBasePath } from '@/lib/paths'

type License = { id: string; type_name: string; business_name: string }

type RecordForm = {
  period_start_date: string
  period_end_date: string
  due_date: string
}

type Props = {
  license: License
  period_start_date: string
  period_end_date: string
  due_date: string
}

export default function LicenseRecordsNew({ license, period_start_date, period_end_date, due_date }: Props) {
  const basePath = useBasePath()

  const form = useForm<RecordForm>({
    period_start_date,
    period_end_date,
    due_date,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(`${basePath}/licenses/${license.id}/records`)
  }

  return (
    <DashboardLayout
      title="New License Period"
      breadcrumbs={[
        { label: 'Licenses', href: 'licenses' },
        { label: license.type_name, href: `licenses/${license.id}` },
        { label: 'New Period' }
      ]}
    >
      <div className="max-w-xl">
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">New License Period</h1>
          <p className="text-sm text-muted-foreground">{license.business_name} · {license.type_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="period_start_date">Period Start Date</Label>
            <Input
              id="period_start_date"
              type="date"
              value={form.data.period_start_date}
              onChange={e => form.setData('period_start_date', e.target.value)}
            />
            {form.errors.period_start_date && <p className="text-xs text-red-600">{form.errors.period_start_date}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="period_end_date">Period End Date</Label>
            <Input
              id="period_end_date"
              type="date"
              value={form.data.period_end_date}
              onChange={e => form.setData('period_end_date', e.target.value)}
            />
            {form.errors.period_end_date && <p className="text-xs text-red-600">{form.errors.period_end_date}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="due_date">Due Date</Label>
            <Input
              id="due_date"
              type="date"
              value={form.data.due_date}
              onChange={e => form.setData('due_date', e.target.value)}
            />
            {form.errors.due_date && <p className="text-xs text-red-600">{form.errors.due_date}</p>}
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button type="submit" disabled={form.processing}>
              Create Period
            </Button>
            <Button type="button" variant="outline" asChild>
              <Link href={`${basePath}/licenses/${license.id}`}>Cancel</Link>
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}
